// utils/ssoAuth.js
import SSO_CONFIG from '../config/ssoConfig';

class SSOAuth {
  constructor(config = {}) {
    this.apiBase = config.apiBase || SSO_CONFIG.API_BASE;
    this.storageKey = config.storageKey || SSO_CONFIG.STORAGE_KEY;
    this.userInfoKey = config.userInfoKey || SSO_CONFIG.USER_INFO_KEY;
  }

  /**
   * 生成随机state字符串用于CSRF防护
   */
  generateState() {
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
  }

  /**
   * 获取SSO登录URL并跳转
   */
  async startLogin(returnUrl = null) {
    try {
      // 直接构造SSO登录URL
      const ssoBaseUrl = SSO_CONFIG.SSO_OAUTH_URL;
      const clientId = SSO_CONFIG.CLIENT_ID;
      const redirectUri = encodeURIComponent(SSO_CONFIG.CALLBACK_URL);
      const responseType = 'code';
      
      // 生成随机state用于CSRF防护
      const state = this.generateState();
      
      // 构造登录URL
      let loginUrl = `${ssoBaseUrl}?client_id=${clientId}&redirect_uri=${redirectUri}&response_type=${responseType}&state=${encodeURIComponent(state)}`;
      
      // 保存state和返回URL到sessionStorage用于回调验证
      sessionStorage.setItem('sso_state', state);
      if (returnUrl) {
        sessionStorage.setItem('sso_return_url', returnUrl);
      }
      
      // 跳转到SSO登录页面
      window.location.href = loginUrl;
      return true;
    } catch (error) {
      console.error('SSO登录启动失败:', error);
      throw error;
    }
  }

  /**
   * 验证state参数防止CSRF攻击
   */
  validateState(receivedState) {
    // 处理统一认证平台直接登录的固定state
    if (receivedState === 'direct_login') {
      return true;
    }
    
    const storedState = sessionStorage.getItem('sso_state');
    if (!storedState) {
      throw new Error('未找到存储的state，可能存在CSRF攻击');
    }
    if (storedState !== receivedState) {
      throw new Error('state参数不匹配，可能存在CSRF攻击');
    }
    // 验证成功后清除存储的state
    sessionStorage.removeItem('sso_state');
    return true;
  }

  /**
   * 处理SSO回调
   */
  async handleCallback(code, state = null, deviceInfo = null) {
    try {
      // 验证state参数防止CSRF攻击
      if (state) {
        this.validateState(state);
      } else {
        console.warn('未提供state参数，跳过CSRF验证');
      }

      const response = await fetch(`${this.apiBase}${SSO_CONFIG.CALLBACK_ENDPOINT}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          code: code,
          deviceId: this.getDeviceId(),
          deviceInfo: deviceInfo || this.getDeviceInfo()
        })
      });

      const data = await response.json();

      if (data.success && data.token) {
        // 保存令牌
        this.setToken(data.token);
        
        // 保存用户信息
        if (data.user) {
          this.setUserInfo(data.user);
        }

        return {
          success: true,
          token: data.token,
          user: data.user,
          ssoLogin: data.ssoLogin || true,
          isNewUser: data.isNewUser || false,
          message: data.message
        };
      } else {
        throw new Error(data.message || 'SSO登录失败');
      }
    } catch (error) {
      console.error('SSO回调处理失败:', error);
      throw error;
    }
  }

  /**
   * 获取当前用户信息
   */
  async getCurrentUser() {
    const token = this.getToken();
    if (!token) {
      throw new Error('未登录');
    }

    try {
      const response = await fetch(`${this.apiBase}${SSO_CONFIG.USER_INFO_ENDPOINT}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (data.success) {
        // 保存最新的用户信息
        this.setUserInfo(data.user);
        
        return {
          user: data.user,
          ssoUser: data.ssoUser // SSO服务器返回的用户信息
        };
      } else {
        throw new Error(data.message || '获取用户信息失败');
      }
    } catch (error) {
      console.error('获取用户信息失败:', error);
      
      // 处理特殊错误状态
      if (error.message.includes('令牌无效') || error.message.includes('过期')) {
        this.logout();
      }
      throw error;
    }
  }

  /**
   * SSO注销
   */
  async logout() {
    const token = this.getToken();
    
    if (token) {
      try {
        const response = await fetch(`${this.apiBase}${SSO_CONFIG.LOGOUT_ENDPOINT}`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        await response.json(); // 忽略响应内容
      } catch (error) {
        console.warn('SSO服务器注销失败:', error);
        // 即使服务器注销失败，也要清除本地数据
      }
    }

    // 清除本地存储
    this.clearStorage();
  }

  /**
   * 检查是否已登录
   */
  isLoggedIn() {
    const token = this.getToken();
    if (!token) return false;

    try {
      // 简单检查JWT是否过期（不验证签名）
      const payload = JSON.parse(atob(token.split('.')[1]));
      const currentTime = Math.floor(Date.now() / 1000);
      
      return payload.exp > currentTime;
    } catch (error) {
      console.warn('令牌格式错误:', error);
      return false;
    }
  }

  /**
   * 发送带认证的请求
   */
  async authenticatedRequest(url, options = {}) {
    const token = this.getToken();
    if (!token) {
      throw new Error('未登录');
    }

    const headers = {
      'Authorization': `Bearer ${token}`,
      ...options.headers
    };

    const response = await fetch(url, {
      ...options,
      headers
    });

    // 检查认证错误
    if (response.status === 401) {
      const errorData = await response.json().catch(() => ({}));
      
      // 处理不同的错误状态码
      if (errorData.code === SSO_CONFIG.ERROR_CODES.KICKED_OUT) {
        alert('您的账号已在其他设备登录');
        this.logout();
        window.location.href = '/login';
        throw new Error('账号已在其他设备登录');
      } else if (errorData.code === SSO_CONFIG.ERROR_CODES.SESSION_EXPIRED) {
        alert('登录已过期，请重新登录');
        this.logout();
        window.location.href = '/login';
        throw new Error('登录已过期');
      } else {
        // 通用认证失败处理
        this.logout();
        window.location.href = '/login';
        throw new Error('认证失败');
      }
    }

    return response;
  }

  /**
   * 测试受保护的路由
   */
  async testProtectedRoute() {
    try {
      const response = await this.authenticatedRequest(`${this.apiBase}${SSO_CONFIG.PROTECTED_ENDPOINT}`);
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('受保护路由测试失败:', error);
      throw error;
    }
  }

  // 工具方法
  getToken() {
    return localStorage.getItem(this.storageKey) || sessionStorage.getItem(this.storageKey);
  }

  setToken(token) {
    localStorage.setItem(this.storageKey, token);
  }

  getUserInfo() {
    const userInfoStr = localStorage.getItem(this.userInfoKey) || sessionStorage.getItem(this.userInfoKey);
    try {
      return userInfoStr ? JSON.parse(userInfoStr) : null;
    } catch (error) {
      console.warn('用户信息解析失败:', error);
      return null;
    }
  }

  setUserInfo(user) {
    localStorage.setItem(this.userInfoKey, JSON.stringify(user));
  }

  clearStorage() {
    localStorage.removeItem(this.storageKey);
    localStorage.removeItem(this.userInfoKey);
    sessionStorage.removeItem(this.storageKey);
    sessionStorage.removeItem(this.userInfoKey);
    sessionStorage.removeItem('sso_return_url');
    sessionStorage.removeItem('sso_state');
  }

  getDeviceId() {
    let deviceId = localStorage.getItem('device_id');
    if (!deviceId) {
      deviceId = 'web_' + Math.random().toString(36).substr(2, 9) + '_' + Date.now();
      localStorage.setItem('device_id', deviceId);
    }
    return deviceId;
  }

  getDeviceInfo() {
    const userAgent = navigator.userAgent;
    const platform = navigator.platform;
    const language = navigator.language;
    
    // 获取屏幕信息
    const screenInfo = window.screen ? {
      width: window.screen.width,
      height: window.screen.height,
      colorDepth: window.screen.colorDepth
    } : {};
    
    return {
      userAgent,
      platform,
      language,
      screen: screenInfo,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      timestamp: new Date().toISOString()
    };
  }
}

// 创建单例实例
const ssoAuth = new SSOAuth();

// 添加调试工具到全局对象（仅在开发环境）
if (process.env.NODE_ENV === 'development') {
  window.ssoAuth = ssoAuth;
  window.ssoDebug = {
    getToken: () => ssoAuth.getToken(),
    getUserInfo: () => ssoAuth.getUserInfo(),
    clearStorage: () => ssoAuth.clearStorage(),
    checkAuth: () => ssoAuth.isLoggedIn(),
    getDeviceInfo: () => ssoAuth.getDeviceInfo(),
    getDeviceId: () => ssoAuth.getDeviceId(),
    getStoredState: () => sessionStorage.getItem('sso_state'),
    generateState: () => ssoAuth.generateState(),
    validateState: (state) => ssoAuth.validateState(state),
    testDirectLogin: () => ssoAuth.validateState('direct_login')
  };
}

export default ssoAuth; 